import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Directive,
    ElementRef,
    HostBinding,
    Inject,
    OnInit,
    ViewChild
} from '@angular/core';
import {
    PanelId,
    ViewId,
    WorkbenchDirection,
    WorkbenchViewConfig
} from '../../types';
import {takeUntil} from 'rxjs/operators';
import {Subject} from "rxjs";
import {faChevronDown, faEllipsisV} from "@fortawesome/pro-solid-svg-icons";
import {UntilDestroy} from "@ngneat/until-destroy";
import {
    faArrowDown,
    faArrowLeft,
    faArrowRight,
    faArrowUp,
    faColumns,
    faExternalLinkSquareAlt,
    faTimes
} from '@fortawesome/pro-light-svg-icons';
import {
    NormalViewContainerState,
    ViewState,
    WorkbenchLayoutManager
} from "../../services/workbench-layout-manager.service";
import {NormalViewContainerConfig, ViewContainerId, WORKBENCH_VIEWCONTAINER_CONFIG} from './viewcontainers';
import {WorkbenchCommandRegistry} from '../../services/workbench-command-registry.service';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {PersistenceService} from '@webmate-web-core/shared/services/persistence.service';
import {FeatureService} from '@webmate-web-core/shared/services/feature.service';
import {WorkbenchContextService} from '../../services/workbench-context.service';

@Directive({selector: '[viewPortal]'})
export class ViewPortal {}

interface TabOrder {
    id: ViewId;
    index: number;
}

@UntilDestroy()
@Component({
    selector: 'wm-workbench-viewcontainer',
    templateUrl: './viewcontainer.component.html',
    styleUrls: ['./viewcontainer.component.less']
})
export class ViewContainerComponent implements OnInit, AfterViewInit {

    viewConfigs: WorkbenchViewConfig<any>[] = [];
    viewIds: Set<ViewId> = new Set<ViewId>([]);

    viewStates: {[viewId: string]: ViewState} = {};

    activeViewContainer: ViewContainerId;

    destroyViewContainer$ = new Subject<void>();

    activeTab?: ViewId;

    tabsOrder: TabOrder[] = [];

    MAX_TABS: number = 15;
    tabIndices: number[] = [];

    scrollable?: boolean = false;

    @ViewChild('scrollView') scrollView: ElementRef;

    @ViewChild('tabheader') tabheaderContainer: ElementRef;

    dotMenuModel: any[] = [];

    @HostBinding('class.active-viewcontainer') isActiveViewContainer: boolean = false;

    @HostBinding('class.tabbed') get isTabbed(): boolean { return this.config.displayMode === 'tabbed'; }

    buildDotMenuModel() {
        if (this.config.moveAllowed) {
            this.dotMenuModel.push({
                label: 'Move to',
                icon: 'fa-arrows-alt',
                items: [
                    {
                        label: 'Left Top',
                        icon: 'fa-arrow-left',
                        command: () => {
                            this.moveViewContainerToPanel('left-top');
                        }
                    },
                    {
                        label: 'Left Bottom',
                        icon: 'fa-arrow-left',
                        command: () => {
                            this.moveViewContainerToPanel('left-bottom');
                        }
                    },
                    {
                        label: 'Bottom',
                        icon: 'fa-arrow-down',
                        command: () => {
                            this.moveViewContainerToPanel('bottom');
                        }
                    },
                ]
            });
        }
        if (this.config.splitAllowed) {
            this.dotMenuModel.push({
                label: 'Split',
                icon: 'fa-columns',
                items: [
                    {
                        label: 'Split Left',
                        icon: 'fa-columns',
                        command: () => {
                            this.splitViewContainer('left');
                        }
                    },
                    {
                        label: 'Split Right',
                        icon: 'fa-columns',
                        command: () => {
                            this.splitViewContainer('right');
                        }
                    },
                    {
                        label: 'Split Up',
                        icon: 'fa-columns',
                        command: () => {
                            this.splitViewContainer('up');
                        }
                    },
                    {
                        label: 'Split Down',
                        icon: 'fa-columns',
                        command: () => {
                            this.splitViewContainer('down');
                        }
                    },
                ]
            }, {
                label: 'Move view',
                icon: faColumns,
                items: [
                    {
                        label: 'Move Left',
                        icon: faArrowLeft,
                        command: () => {
                            this.moveActiveView('left');
                        }
                    },
                    {
                        label: 'Move Right',
                        icon: faArrowRight,
                        command: () => {
                            this.moveActiveView('right');
                        }
                    },
                    {
                        label: 'Move Up',
                        icon: faArrowUp,
                        command: () => {
                            this.moveActiveView('up');
                        }
                    },
                    {
                        label: 'Move Down',
                        icon: faArrowDown,
                        command: () => {
                            this.moveActiveView('down');
                        }
                    }
                ]
            });
        }

        this.featureService.getFeature('webmate.auditlab').subscribe(f => {
            const isChildWorkbench = this.workbenchContextService.getCurrentWorkbenchContext()?.isChildWorkbench;
            if (f.isActive && !!this.config.popoutAllowed && !isChildWorkbench) {
                this.dotMenuModel.push({
                    label: 'New Window',
                    icon: faExternalLinkSquareAlt,
                    command: () => {
                        this.moveActiveView('newWindow');
                    }
                });
            }
        });

        if (this.config.hideAllowed) {
            this.dotMenuModel.push({
                label: 'Hide',
                icon: 'fa-eye-slash',
                command: () => {
                    setTimeout(() => {
                        this.hideViewContainer();
                    })
                }
            });
        }
    }

    constructor(private layoutManager: WorkbenchLayoutManager,
                @Inject(WORKBENCH_VIEWCONTAINER_CONFIG) public config: NormalViewContainerConfig,
                private persistenceService: PersistenceService,
                private featureService: FeatureService,
                private cdr: ChangeDetectorRef,
                private readonly workbenchContextService: WorkbenchContextService,
                private commandRegistry: WorkbenchCommandRegistry) {
        for (let i = 0; i < this.MAX_TABS; i++) {
            this.tabIndices.push(i);
        }
    }

    toggleView(viewId: ViewId) {
        this.viewStates[viewId].expanded = !this.viewStates[viewId].expanded;
        this.layoutManager.setViewExpansionState(viewId,
            this.viewStates[viewId].expanded || false);
    }

    ngOnInit() {
        this.layoutManager.subscribeToNormalViewContainerChanges$(this.config.id).pipe(takeUntil(this.destroyViewContainer$)).subscribe((viewContainerState: NormalViewContainerState) => {
            this.viewConfigs = this.layoutManager.getVisibleViewConfigsForViewContainer(this.config.id);
            this.orderTabs();
            this.viewIds = new Set<ViewId>(this.viewConfigs.map(viewConfig => viewConfig.id));
            if (this.config.displayMode === 'tabbed') {
                if (viewContainerState.activeTab && this.viewIds.has(viewContainerState.activeTab)) {
                    this.activeTab = viewContainerState.activeTab;
                    this.makeActiveViewContainer();
                } else if ((this.activeTab === undefined || !this.viewIds.has(this.activeTab)) && this.viewConfigs[this.viewConfigs.length - 1]) {
                    this.activateTab(this.viewConfigs[this.viewConfigs.length - 1]?.id)
                }
            }

            this.cdr.markForCheck();
        });

        this.layoutManager.subscribeActiveContainerId$().pipe(takeUntil(this.destroyViewContainer$)).subscribe((activeCenterContainerId: ViewContainerId) => {
            this.activeViewContainer = activeCenterContainerId;
            this.isActiveViewContainer = this.activeViewContainer === this.config.id;
            this.cdr.markForCheck();
        });

        this.layoutManager.subscribeActiveViewId$().pipe(takeUntil(this.destroyViewContainer$)).subscribe((activeViewId: ViewId) => {
            if (this.viewIds.has(activeViewId)) {
                if (this.config.displayMode === 'tabbed' && this.activeTab !== activeViewId) {
                    this.activateTab(activeViewId)
                }
                this.cdr.markForCheck();
            }
        });

        this.layoutManager.subscribeToViewEvents$(undefined, false).pipe(takeUntil(this.destroyViewContainer$)).subscribe((viewState) => {
            this.viewStates[viewState.viewId] = viewState;
            this.cdr.markForCheck();
        });

        this.buildDotMenuModel();
    }

    ngAfterViewInit() {
        // Add a click listener to set the view container active immeditaly on click
        // This has to be done manually, because we want to handle the event in the capture phase,
        // i.e. before all regular handlers are triggered. Otherwise, the view container is set to active after
        // all other actions have been executed, which may undo setting another view container active
        this.scrollView.nativeElement.addEventListener('click', () => {
            if (!this.isActiveViewContainer) {
                this.makeActiveViewContainer();
            }
        }, true)

        // In tabbed mode, convert vertical scrolling in tab header into horizontal scrolling
        if (this.config.displayMode === 'tabbed') {
            const self = this;
            this.tabheaderContainer.nativeElement.addEventListener("wheel", function (e) {
                if (e.deltaY > 0) {
                    self.tabheaderContainer.nativeElement.scrollLeft += 50;
                    e.preventDefault();
                }
                else {
                    self.tabheaderContainer.nativeElement.scrollLeft -= 50;
                    e.preventDefault();
                }
            });
        }
    }

    makeActiveViewContainer() {
        this.layoutManager.activateViewContainer(this.config.id, this.activeTab);
    }

    hideViewContainer() {
        this.layoutManager.hideViewContainer(this.config.id);
    }

    moveViewContainerToPanel(panelId: PanelId) {
        setTimeout(() => { // important because this view container may be detached from DOM in the process and then the menu won't disappear.
            this.layoutManager.showViewContainerInPanel(this.config.id, panelId);
        });
    }

    splitViewContainer(direction: WorkbenchDirection) {
        setTimeout(() => { // important because this view container may be detached from DOM in the process and then the menu won't disappear.
            this.layoutManager.splitViewContainer(this.config.id, direction);
        });
    }

    moveActiveView(direction: WorkbenchDirection) {
        this.layoutManager.moveViewToDirection(this.config.id, this.activeTab, direction);
    }

    activateTab(viewId: ViewId) {
        this.activeTab = viewId;
        this.layoutManager.setViewContainerActiveTab(this.config.id, viewId);
        if (!this.isActiveViewContainer) {
            this.makeActiveViewContainer();
        }
    }

    closeView(viewId: ViewId) {
        this.layoutManager.closeView(viewId);
    }

    getScrollView(): ElementRef{
        return this.scrollView;
    }

    orderTabs() {
        let tabs = this.viewConfigs.map(conf => conf.id);
        const existingTabOrder = this.persistenceService.getObjectItem(`tabs-order-${this.config.id}`) as TabOrder[];
        if (!!existingTabOrder) {
            tabs.sort((a, b) => {
                const idx1 = existingTabOrder.findIndex(tabOrder => tabOrder.id == a);
                const idx2 = existingTabOrder.findIndex(tabOrder => tabOrder.id == b);
                const pos1 = idx1 === -1 ? Number.MAX_VALUE : idx1;
                const pos2 = idx2 === -1 ? Number.MAX_VALUE : idx2;
                return pos1 - pos2;
            });
        }

        if (tabs.length > this.MAX_TABS) {
            const oldestTab = this.layoutManager.getOldestView(this.config.id);
            if (!!oldestTab) {
                this.closeView(oldestTab.id);
            }
            return;
        }

        this.tabsOrder = tabs.map(viewId => ({ id: viewId, index: this.viewConfigs.findIndex(conf => conf.id == viewId)}));
        this.persistTabOrder();
    }

    tabDropped(event: CdkDragDrop<number[]>) {
        moveItemInArray(this.tabsOrder, event.previousIndex, event.currentIndex);
        this.persistTabOrder();
    }

    persistTabOrder() {
        this.persistenceService.setObjectItem(`tabs-order-${this.config.id}`, this.tabsOrder);
    }

    onFilesDropped(files: File[]) {
        files.forEach(file =>
            this.commandRegistry.executeCommand('workbench:openLocalFile', { file })
        );
    }

    protected readonly faEllipsisV = faEllipsisV;
    protected readonly faTimes = faTimes;
    protected readonly faChevronDown = faChevronDown;
}
